import { useState } from 'react'
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Loader2,
  Minus,
  Plug,
  PlugZap,
  Plus,
  Search,
} from 'lucide-react'
import { ToolErrorGuidance } from '../ToolErrorGuidance'

interface ToolPartProps {
  part: any
}

function getToolState(part: any) {
  if (part.type === 'tool-input-error') {
    return {
      isComplete: true,
      isError: true,
      result: null,
      args: part.input ?? {},
    }
  }
  if (part.state) {
    const isComplete =
      part.state === 'output-available' || part.state === 'error'
    const isError = part.state === 'error'
    let result: any = null
    if (isComplete && part.output != null) {
      try {
        result =
          typeof part.output === 'string'
            ? JSON.parse(part.output)
            : part.output
      } catch {
        /* ignore */
      }
    }
    return { isComplete, isError, result, args: part.input ?? {} }
  }
  const ti = part.toolInvocation
  if (ti) {
    const isComplete = ti.state === 'result' || ti.state === 'error'
    const isError = ti.state === 'error'
    let result: any = null
    if (isComplete && ti.result) {
      try {
        result =
          typeof ti.result === 'string' ? JSON.parse(ti.result) : ti.result
      } catch {
        /* ignore */
      }
    }
    return { isComplete, isError, result, args: ti.args ?? {} }
  }
  return { isComplete: false, isError: false, result: null, args: {} }
}

function isToolError(isComplete: boolean, result: any): boolean {
  if (!isComplete || !result) return false
  return result.ok === false || result.success === false || !!result.error
}

function getErrorMessage(result: any): string | null {
  if (!result) return null
  return result.error ?? result.message ?? result.errorCode ?? null
}

const ACTION_LABELS: Record<string, { loading: string; done: string }> = {
  listActions: {
    loading: 'Listing procedure actions...',
    done: 'Procedure actions listed',
  },
  describeAction: {
    loading: 'Inspecting action...',
    done: 'Action details loaded',
  },
  searchActions: {
    loading: 'Searching procedure actions...',
    done: 'Search complete',
  },
  listConnectedProviders: {
    loading: 'Checking integrations...',
    done: 'Integrations checked',
  },
}

function summarizeQuery(args: any): string | null {
  const parts: string[] = []
  if (typeof args?.query === 'string' && args.query.trim()) {
    parts.push(`"${args.query.trim()}"`)
  }
  if (Array.isArray(args?.providers) && args.providers.length > 0) {
    parts.push(`provider: ${args.providers.join(', ')}`)
  }
  const controlRefs = args?.frameworkControlRefs ?? args?.controlRefs
  if (Array.isArray(controlRefs) && controlRefs.length > 0) {
    parts.push(`controls: ${controlRefs.join(', ')}`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

function ConnectionDot({
  connected,
}: {
  connected: boolean | null | undefined
}) {
  if (connected === null || connected === undefined) {
    return (
      <span className='inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-status-gray' />
    )
  }
  return (
    <span
      className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
        connected ? 'bg-status-green' : 'bg-status-gray'
      }`}
    />
  )
}

export function RunbookCatalogToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action: string = args?.action ?? 'listActions'
  const labels =
    ACTION_LABELS[action] ?? { loading: 'Loading catalog...', done: 'Catalog loaded' }
  const [expanded, setExpanded] = useState(false)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='runbookCatalog'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  // Loading state
  if (!isComplete) {
    const detail = action === 'searchActions' ? summarizeQuery(args) : null
    return (
      <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
        <span className='truncate'>
          {labels.loading}
          {detail ? ` · ${detail}` : ''}
        </span>
      </div>
    )
  }

  // describeAction — compact single-line summary
  if (action === 'describeAction' && result) {
    const toolId = result.toolId ?? args?.toolId
    return (
      <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
        <BookOpen className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
        <span className='truncate'>
          Found action: <span className='font-mono'>{toolId}</span>
          {result.description ? `: ${result.description}` : ''}
        </span>
      </div>
    )
  }

  // listConnectedProviders — collapsible list with summary header
  if (action === 'listConnectedProviders' && result) {
    const providers = Array.isArray(result.providers) ? result.providers : []
    const connectedProviders = providers.filter((p: any) => p.connected)
    const connectedCount = connectedProviders.length

    if (connectedCount === 0) {
      return (
        <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
          <PlugZap className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
          <span className='truncate'>No connected providers</span>
        </div>
      )
    }

    return (
      <div className='text-muted-foreground py-1 text-xs'>
        <button
          type='button'
          onClick={() => setExpanded((v) => !v)}
          className='group/row flex w-full cursor-pointer items-center gap-2'
          aria-expanded={expanded}
        >
          {expanded ? (
            <Minus className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
          ) : (
            <>
              <PlugZap className='h-3.5 w-3.5 shrink-0 text-text-subtle group-hover/row:hidden' />
              <Plus className='hidden h-3.5 w-3.5 shrink-0 text-text-subtle group-hover/row:block' />
            </>
          )}
          <span className='flex-1 truncate text-left'>
            Found {connectedCount} connected provider
            {connectedCount === 1 ? '' : 's'}
          </span>
          {expanded ? (
            <ChevronDown className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
          ) : (
            <ChevronRight className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
          )}
        </button>
        {expanded && (
          <div className='mt-1.5 ml-5 space-y-1'>
            {connectedProviders.map((p: any) => (
              <div
                key={p.provider}
                className='flex items-center gap-2 text-xs'
              >
                <ConnectionDot connected={p.connected} />
                <span className='text-text-secondary'>
                  {p.providerLabel ?? p.provider}
                </span>
                <span className='ml-auto text-[10px] text-text-placeholder'>
                  {p.actionCount ?? 0} action
                  {(p.actionCount ?? 0) === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // listActions — compact summary only (no expandable list)
  if (action === 'listActions' && result && Array.isArray(result.actions)) {
    const actions = result.actions
    const total = result.total ?? actions.length
    const returned = result.returned ?? actions.length
    return (
      <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
        <BookOpen className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
        <span className='truncate'>
          Found {returned}/{total} available actions
        </span>
      </div>
    )
  }

  // searchActions — collapsed list. Header shows "Found N actions for '{query}'";
  // click to expand the full list inline. No preview rows, no table — keeps the
  // chat scannable when Vera fans out multiple catalog searches per draft.
  if (action === 'searchActions' && result && Array.isArray(result.actions)) {
    const actions = result.actions
    const total = result.total ?? actions.length
    const returned = result.returned ?? actions.length
    const querySummary = summarizeQuery(args)

    if (actions.length === 0) {
      return (
        <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
          <Search className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
          <span className='truncate'>
            No matching actions
            {querySummary ? ` for ${querySummary}` : ''}
          </span>
        </div>
      )
    }

    const headerLabel = querySummary
      ? `Found ${returned} action${returned === 1 ? '' : 's'} for ${querySummary}`
      : `Found ${returned} action${returned === 1 ? '' : 's'}`
    const remainder =
      total > returned ? ` (of ${total} total)` : ''

    return (
      <div className='text-muted-foreground py-1 text-xs'>
        <button
          type='button'
          onClick={() => setExpanded((v) => !v)}
          className='group/row flex w-full cursor-pointer items-center gap-2'
          aria-expanded={expanded}
        >
          {expanded ? (
            <Minus className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
          ) : (
            <>
              <Search className='h-3.5 w-3.5 shrink-0 text-text-subtle group-hover/row:hidden' />
              <Plus className='hidden h-3.5 w-3.5 shrink-0 text-text-subtle group-hover/row:block' />
            </>
          )}
          <span className='flex-1 truncate text-left'>
            {headerLabel}
            {remainder && (
              <span className='text-text-placeholder'>{remainder}</span>
            )}
          </span>
          {expanded ? (
            <ChevronDown className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
          ) : (
            <ChevronRight className='h-3.5 w-3.5 shrink-0 text-text-subtle' />
          )}
        </button>
        {expanded && (
          <div className='mt-1.5 ml-5 space-y-1'>
            {actions.map((a: any, i: number) => {
              const toolId =
                a.toolId ??
                (a.action && Array.isArray(a.providers) && a.providers[0]
                  ? `${a.providers[0]}.${a.action}`
                  : a.action)
              return (
                <div key={toolId ?? i} className='text-xs'>
                  <div className='flex items-center gap-2'>
                    <ConnectionDot connected={a.connected} />
                    <span className='font-mono text-[11px] text-text-secondary'>
                      {toolId}
                    </span>
                    {a.name && (
                      <span className='truncate text-[11px] text-text-body'>
                        {a.name}
                      </span>
                    )}
                  </div>
                  {a.description && (
                    <p className='mt-0.5 line-clamp-2 pl-3.5 text-[11px] text-text-subtle'>
                      {a.description}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // Generic completion
  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      <Plug className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      <span className='truncate'>{labels.done}</span>
    </div>
  )
}
